import store from 'store'
import AuthService from '@hotmart-org-ca/cas-js'
import { PossibleStorages } from '@hotmart-org-ca/cas-js/dist/auth.service'
import Axios from 'axios'
import log from 'app/log.js'
import { categories } from 'app/core/constants.js'
import { buildQueryString } from 'app/core/utils/buildQueryString.js'
import authConst from './authConst.js'
import AuthManager from './AuthManager.js'

const casConfig = {
  client_id: process.env.CAS_CLIENT_ID,
  scope: 'openid profile user authorities email'
}

const authService = new AuthService(casConfig)
const authSurrogateService = new AuthService({
  ...casConfig,
  authority: `${process.env.SURROGATE_URL}/oidc`,
  storeToUse: PossibleStorages.localStorage
})

const authManager = new AuthManager(authService, authSurrogateService, authConst.casProviderKey, authConst.surrogateProvider)

const profileApi = Axios.create({
  baseURL: process.env.APP_API
})

const productApi = Axios.create({
  baseURL: process.env.API_PRODUCT
})

const businessWorkspaceApi = Axios.create({
  baseURL: process.env.API_BUSINESS_WORKSPACE
})

export default {
  onUserLoaded (fn) {
    authManager.getService().userManager.events.addUserLoaded(fn)
  },
  redirectToLogin () {
    authManager.getService().signinRedirect()
  },
  processLoginResponse () {
    return authManager.getService().signinCallback()
  },
  processLoginRenewResponse () {
    return authManager.getService().signinCallback()
  },
  redirectToLogout () {
    authManager.getService().signoutRedirect()
  },
  processLogoutResponse () {
    return authManager.getService().signoutCallback().then(() => {
      authManager.useDefault()
    })
  },
  processSurrogateLogin () {
    authManager.useSurrogate()
  },
  async getLegacyUserData () {
    try {
      let legacyUserData = await Axios.get('userprofile/rest/v1/user/')
      const { name, id, roles, locale, email } = legacyUserData.data
      let authorities = []
      if (roles && roles.length) {
        authorities = roles.map(role => role.name)
      }
      // mimic the same signature as the CAS user object
      return {
        _legacy: true,
        profile: {
          name,
          id,
          authorities,
          locale,
          email
        }
      }
    } catch (error) {
      throw error
    }
  },
  getUserData () {
    return authManager.getService().getUser()
  },
  async getToken () {
    const userData = await this.getUserData()
    return userData && userData.id_token
  },
  async getProductData (productId, queryParams = {}) {
    const baseUrl = process.env.COMPONENT_API
    const url = `${baseUrl}product/${productId}${buildQueryString(queryParams)}`
    return Axios.get(url)
  },
  hasRole (role) {
    const { user } = store.state.auth
    if (!user || !user.authorities) {
      return false
    }
    return user.authorities.includes(role)
  },
  async getProducts () {
    const { data } = await Axios.get(process.env.COMPONENT_API + 'product')
      .catch((error) => {
        log({
          error
        })
        return { data: [] }
      })
    return data
  },
  async getUserProfileImageUrl () {
    const response = await profileApi.get('/userprofile/rest/v1/user/profile')
    return response.data?.urlPhoto ?? ''
  },
  async getProductWithAllData (pageToFetch, rowsToFetch) {
    const { data: { data, page, size } } = await profileApi.get(`marketplace/rest/v2/product/producer?page=${pageToFetch}&rows=${rowsToFetch}`)
    const products = data?.map(product => ({...product, category: categories[product.category]})) ?? []
    return {products, page, size}
  },
  async getProductsByName (name) {
    const { data: { data, page, size } } = await profileApi.get(`marketplace/rest/v2/product/producer?name=${name}`)
    if (!data) {
      return {products: []}
    }
    const products = data?.map(product => ({...product, category: categories[product.category]}))

    return {products, page, size}
  },
  async getPlansByProduct (productId) {
    try {
      const { data: plans } = await productApi.get(`product/v1/product/${productId}/plans?summary=true&page=1`)

      return plans
    } catch (e) {
      console.error(e)
      return []
    }
  },
  async getOffersByPlan (productId, planId) {
    try {
      const { data: offers } = await productApi.get(`product/v1/product/${productId}/plan/offers?plans[]=${planId}&hideAuxiliaryOffers=true&summary=true`)

      return offers
    } catch (e) {
      console.error(e)
      return []
    }
  },
  async getPaymentMode ({ currencyCode, offerId, periodicity, productId }) {
    try {
      const { data: paymentMode } = await productApi.get(`/product/v1/product/payment-mode?currency=BRL&offerId=${offerId}&periodicity=${periodicity}&productId=${productId}&formatId=2`, {
        currency: currencyCode,
        offerId,
        periodicity,
        productId,
        formatId: 2,
        isSubsInstallments: true
      })

      return paymentMode
    } catch (e) {
      console.error(e)
    }
  },
  setProfileApiAuthenticationHeader (token) {
    profileApi.defaults.headers.common['Authorization'] = token
  },
  setProductApiAuthenticationHeader (token) {
    productApi.defaults.headers.common['Authorization'] = 'Bearer ' + token
  },
  setBusinessWorkspaceApiAuthenticationHeader (token) {
    businessWorkspaceApi.defaults.headers.common['Authorization'] = 'Bearer ' + token
  },
  async getUserTypeInfo () {
    try {
      const { data = {} } = await businessWorkspaceApi.get('/v1/user/type-info')
      const { salesProfileUserType } = data
      return { salesProfileUserType }
    } catch (e) {
      console.error(e)
      return {}
    }
  }

}
